/**
 * screens/Reconciliation — Finance
 * ──────────────────────────────────────────────────────────────────────────
 * Counted cash against the ledger. Variance is always routed.
 *
 * This screen is GATED on variance routing. Until Finance has said where a
 * shortfall and a surplus each go, a count would have nowhere to post its
 * difference — and invariant 3 forbids absorbing it. The API answers 412 with
 * `details.gate: 'variance_set'`; useAsyncData turns that into `gate`, and the
 * screen shows the educational panel with an amber bar rather than an error.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { EmptyState, Loading, PageContainer, PageHeader } from '../../components';
import { useAsyncData } from '../../lib/useAsyncData';
import { fetchReconciliations } from '../../services/reconciliation/reconciliation.service';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import ReconCard from './components/ReconCard';
import { RECON_COPY, RECON_FLOW, RECON_STEPS } from './emptyState';

export function ReconciliationScreen() {
  const { dataVersion, openDrawer, openModal, setView } = useShell();
  const { gates } = useConfig();

  const load = useCallback(() => fetchReconciliations(), []);
  const { data, loading, gate: gateFrom412 } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load reconciliations',
  });

  /*
   * WHERE THE GATE COMES FROM — verified against the live API.
   *
   * This endpoint answers 200 with the gate map in the BODY; it does not 412.
   * That is the right call on the server's part (an unconfigured workspace can
   * still see its empty list), but it means the gate has to be read from the
   * payload. `gateFrom412` is kept as a second source because a gate can also
   * arrive that way from the WRITE routes, and bootstrap's copy covers the
   * first render before this response lands.
   */
  const gate =
    data?.gates.variance_set === false || (!data && gates.variance_set === false)
      ? 'variance_set'
      : gateFrom412;

  if (loading && !data) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  const rows = data?.reconciliations ?? [];

  return (
    <PageContainer testID="screen-recon">
      <PageHeader
        title="Reconciliation"
        subtitle="Counted cash against the ledger. Variance is always routed."
      />

      {gate || rows.length === 0 ? (
        <EmptyState
          eyebrow={RECON_COPY.eyebrow}
          title={gate ? 'Reconciliation is not configured yet' : RECON_COPY.title}
          blurb={RECON_COPY.blurb}
          nodes={RECON_FLOW}
          flowNote={RECON_COPY.flowNote}
          steps={RECON_STEPS}
          gate={gate ?? undefined}
          gateNote={gate ? RECON_COPY.gateNote : undefined}
          onResolveGate={gate ? () => setView('settings') : undefined}
          footNote={RECON_COPY.footNote}
          ctaLabel={gate ? undefined : 'Open the register'}
          onCta={gate ? undefined : () => setView('register')}
        />
      ) : (
        <View style={styles.list}>
          {rows.map((recon) => (
            <ReconCard
              key={recon.id}
              recon={recon}
              onViewLedger={() => openDrawer(recon.assignment_id)}
              onWaive={() =>
                openModal({ kind: 'resolveVariance', reconId: recon.id, action: 'waive' })
              }
              onRoute={() =>
                openModal({ kind: 'resolveVariance', reconId: recon.id, action: 'route' })
              }
            />
          ))}
        </View>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  // 12px between count cards, from the design source.
  list: { gap: 12 },
});

export default ReconciliationScreen;
